import React, { useMemo, useState, useEffect } from "react";
import { gql } from "@src/__generated__";
import { useLazyQuery, useQuery } from "@apollo/client";
import { AsyncSelect } from "@src/components/elements/Select/AsyncSelect";
import { MultiValue } from "react-select";
import { AsyncMultiSelect } from "@src/components/elements/Select/AsyncMultiSelect";
import { SelectOption } from "@src/components/elements/Select/SelectOption";
import { IntakeQuestionType } from "@src/__generated__/graphql";
import { useSudoziMutation } from "@src/infrastructure/useSudoziMutation";
import {
  GetPublicIntakeQuestionOptionsRoute,
  PostVendorsByNameRoute,
} from "@src/sudozi-schemas/api-route-schemas";
import { useSudoziPublicQuery } from "@src/infrastructure/useSudoziQuery";
import { sudoziFetchBase } from "@src/infrastructure/apiCall";
import { useSession } from "@src/contexts/SessionContext";

type AsyncSelectProps =
  | {
      type: "single";
      fieldType: IntakeQuestionType;
      field: { questionId: string };
      value: string | null;
      error?: string | undefined;
      onChange: (option: SelectOption | null) => void;
      className?: string;
      disabled?: boolean;
      onOptionCreated?: (option: SelectOption) => void;
    }
  | {
      type: "multi";
      fieldType: IntakeQuestionType;
      field: { questionId: string };
      value: string[];
      error?: string | undefined;
      onChange: (option: MultiValue<SelectOption>) => void;
      className?: string;
      disabled?: boolean;
      onOptionCreated?: (option: SelectOption) => void;
    };

const GET_OPTIONS_FOR_QUESTION = gql(/* GraphQL */ `
  query GetSubmissionOptionsForQuestion(
    $intakeQuestionId: String
    $search: String
    $includeIds: [String!]
  ) {
    optionsForIntakeQuestion(
      intakeQuestionId: $intakeQuestionId
      search: $search
      includeIds: $includeIds
    ) {
      id
      label
    }
  }
`);

export function IntakeSubmissionOptionSelect({
  type,
  fieldType,
  value,
  onChange,
  field,
  error,
  className,
  disabled = false,
  onOptionCreated,
}: AsyncSelectProps) {
  const { currentCompany, vendorCompany } = useSession();
  const isPublic = !currentCompany && !vendorCompany;
  const { data } = useQuery(GET_OPTIONS_FOR_QUESTION, {
    variables: {
      intakeQuestionId: field.questionId,
    },
    skip: isPublic,
  });

  const { data: optionsForValue } = useQuery(GET_OPTIONS_FOR_QUESTION, {
    variables: {
      intakeQuestionId: field.questionId,
      includeIds: value ? (Array.isArray(value) ? value : [value]) : null,
    },
    skip: isPublic || !value,
  });

  const { data: publicQuestionOptionsData } = useSudoziPublicQuery({
    route: GetPublicIntakeQuestionOptionsRoute,
    queryKey: [GetPublicIntakeQuestionOptionsRoute.path, field.questionId],
    params: { intakeQuestionId: field.questionId },
    query: {},
    options: {
      enabled: isPublic,
    },
  });

  const createVendor = useSudoziMutation({
    route: PostVendorsByNameRoute,
    mutationKey: "createVendor",
  });

  const [_searchOptionsForIntakeQuestion] = useLazyQuery(
    GET_OPTIONS_FOR_QUESTION,
  );

  const searchOptionsForIntakeQuestion = async (search: string) => {
    if (isPublic) {
      const response = await sudoziFetchBase({
        route: GetPublicIntakeQuestionOptionsRoute,
        params: { intakeQuestionId: field.questionId },
        query: { search },
      });

      const parsed = GetPublicIntakeQuestionOptionsRoute.output.parse(
        await response.json(),
      );

      return parsed;
    }
    const { data } = await _searchOptionsForIntakeQuestion({
      variables: {
        intakeQuestionId: field.questionId,
        search,
      },
    });
    return (
      data?.optionsForIntakeQuestion.map(({ id, label }) => ({
        label,
        value: id,
      })) ?? []
    );
  };

  const defaultOptions = useMemo<SelectOption[]>(
    () =>
      isPublic
        ? publicQuestionOptionsData ?? []
        : (
            data?.optionsForIntakeQuestion.map((option) => ({
              label: option.label,
              value: option.id,
            })) ?? []
          ).concat(
            (optionsForValue?.optionsForIntakeQuestion ?? []).map((option) => ({
              label: option.label,
              value: option.id,
            })),
          ),
    [
      data?.optionsForIntakeQuestion,
      isPublic,
      publicQuestionOptionsData,
      optionsForValue,
    ],
  );

  // if the selected value(s) are not in the initial page of options loaded, store them here
  const [selectedOptions, setSelectedOptions] = useState<SelectOption[]>([]);

  const options = React.useMemo(() => {
    const uniqueOptions = new Map<string, SelectOption>();
    [...defaultOptions, ...selectedOptions].forEach((option) => {
      uniqueOptions.set(option.value, option);
    });
    return Array.from(uniqueOptions.values());
  }, [defaultOptions, selectedOptions]);

  // Add effect to select first option when there is only one option and no value is selected
  useEffect(() => {
    if (options.length === 1) {
      if (type === "single" && !value) {
        onChange(options[0]);
      } else if (type === "multi" && (!value || value.length === 0)) {
        onChange([options[0]]);
      }
    }
  }, [options, type, value, onChange]);

  return type === "single" ? (
    <AsyncSelect
      isCreatable={fieldType === IntakeQuestionType.VENDOR_SELECT}
      isClearable={true}
      error={error}
      loadOptions={searchOptionsForIntakeQuestion}
      value={options.find((option) => value === option.value) ?? null}
      defaultOptions={options}
      onChange={(option) => {
        setSelectedOptions((prev) => {
          const newSelectedOptions = [...prev];
          if (option?.value && !prev.find((o) => o.value === option.value)) {
            newSelectedOptions.push({ ...option });
          }
          return newSelectedOptions;
        });
        onChange(option);
      }}
      menuPortalTarget={document.body}
      className={`${error ? "field-error" : ""} ${className ?? ""}`}
      onCreateOption={async (inputString: string): Promise<void> => {
        if (fieldType === IntakeQuestionType.VENDOR_SELECT) {
          const { id } = await createVendor.mutateAsync({
            body: {
              name: inputString,
            },
          });
          setSelectedOptions((prev) => {
            const newSelectedOptions = [...prev];
            if (!prev.find((o) => o.value === id)) {
              newSelectedOptions.push({ label: inputString, value: id });
            }
            return newSelectedOptions;
          });
          onOptionCreated?.({
            label: inputString,
            value: id,
          });
        }
      }}
      isDisabled={disabled}
    />
  ) : (
    <AsyncMultiSelect
      isCreatable={fieldType === IntakeQuestionType.VENDOR_SELECT}
      isClearable={true}
      error={error}
      loadOptions={searchOptionsForIntakeQuestion}
      value={options.filter((o) => value?.includes(o.value))}
      defaultOptions={options}
      onChange={(options) => {
        setSelectedOptions((prev) => {
          const newSelectedOptions = [...prev];
          options.forEach((option) => {
            if (!prev.find((o) => o.value === option.value)) {
              newSelectedOptions.push({ ...option });
            }
          });
          return newSelectedOptions;
        });
        onChange(options);
      }}
      menuPortalTarget={document.body}
      className={className}
      onCreateOption={async (inputString: string): Promise<void> => {
        if (fieldType === IntakeQuestionType.VENDOR_SELECT) {
          const { id } = await createVendor.mutateAsync({
            body: {
              name: inputString,
            },
          });
          setSelectedOptions((prev) => {
            const newSelectedOptions = [...prev];
            if (!prev.find((o) => o.value === id)) {
              newSelectedOptions.push({ label: inputString, value: id });
            }
            return newSelectedOptions;
          });
          onOptionCreated?.({
            label: inputString,
            value: id,
          });
        }
      }}
      isDisabled={disabled}
    />
  );
}
